import React, { useEffect, useState } from 'react';

import { Button, Center, Divider, Group, Loader, Skeleton, Stack, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconLock } from '@tabler/icons-react';
import DiscountCodeInput from 'components/Discounts/DiscountCodeInput';
import BillingPlanSummary from 'components/SalesPortal/BillingPlanSummary';
import PackageSummary from 'components/SalesPortal/PackageSummary';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useCreateAthletePackage from 'hooks/athletePackage/useCreateAthletePackage';
import { useTracking } from 'hooks/mixpanel/useTracking';
import usePaymentMethod from 'hooks/paymentMethod/usePaymentMethod';
import { BillingPlanRetrieve, DiscountRetrieve, SalesPortalPackageRetrieve } from 'types';
import { NonStripePaymentMethod } from 'types/enums';
import { getSuccessorStartDate } from 'utils/date-functions';
import { isFirstInstallmentFree } from 'utils/discount.helpers';
import { getPaymentMethodName } from 'utils/formatters';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

import CheckoutFlowStates from '../checkoutFlowStates';

interface FormValues {
  package: SalesPortalPackageRetrieve;
  billingPlan: BillingPlanRetrieve;
  startDate: Date;
  billingStartDate: Date;
  autoRenew: boolean;
  autoPay: boolean;
}

interface IReviewProps {
  athleteId: string;
  fullName: string;
  packageData: SalesPortalPackageRetrieve;
  billingPlan: BillingPlanRetrieve;
  paymentMethod: string;
  setState: React.Dispatch<React.SetStateAction<CheckoutFlowStates>>;
  setNoPaymentTaken: (noPaymentTaken: boolean) => void;
  handleClose: () => void;
}

const Review = ({
  athleteId,
  fullName,
  packageData,
  billingPlan,
  paymentMethod,
  setState,
  setNoPaymentTaken,
  handleClose,
}: IReviewProps): JSX.Element => {
  const { dateFormat } = useLocalization(packageData.gym.country);

  const createMutation = useCreateAthletePackage();
  const { trackSalesPortalPurchaseCompleted } = useTracking();

  const [appliedDiscount, setAppliedDiscount] = useState<DiscountRetrieve | null>(null);

  const firstInstallmentPriceCents =
    billingPlan.installments?.find(i => i.installmentNumber === 1)?.price ?? 0;

  const successorPackage = packageData?.successorPackage;
  if (successorPackage) {
    successorPackage.startDate = getSuccessorStartDate(
      dateFormat,
      packageData.attendanceRules,
      packageData.startDate || undefined,
    );
  }
  const successorBillingPlan = packageData?.successorBillingPlan;

  const { data, isSuccess, isLoading, isError } = usePaymentMethod(paymentMethod);

  const form = useForm<FormValues>({
    initialValues: {
      startDate: packageData?.startDate ? new Date(packageData.startDate) : new Date(),
      billingStartDate: packageData?.startDate ? new Date(packageData.startDate) : new Date(),
      autoRenew: true,
      autoPay: true,
      package: packageData,
      billingPlan: billingPlan,
    },
    validate: {
      package: value => (!value ? 'Cant create without package' : null),
      billingPlan: value => (!value ? 'Cant create without billing plan' : null),
    },
  });

  const handleCloseAndReset = (): void => {
    handleClose();
    form.reset();
  };

  const getPackageStartDate = (): JSX.Element => {
    if (packageData?.startDate) {
      return (
        <Text size='sm' color='dimmed'>
          {dayjs(packageData.startDate).format('MMMM D, YYYY')}
        </Text>
      );
    }
    return (
      <Text size='sm' color='dimmed'>
        {dayjs().format('MMMM D, YYYY')}
      </Text>
    );
  };

  const handleSubmit = (): void => {
    if (form.values.package && form.values.billingPlan) {
      const { errorNotification } = notificationHandler();
      const payload = {
        ...form.values,
        athletePackageId: uuidv4(),
        athlete: athleteId,
        package: form.values.package.packageId,
        athleteStatusOnStart: form.values.package.athleteStatusOnStart,
        athleteStatusOnEnd: form.values.package.athleteStatusOnEnd,
        billingPlan: form.values.billingPlan.billingPlanId,
        startDate: dayjs(form.values.startDate).format('YYYY-MM-DD'),
        billingStartDate: dayjs(form.values.billingStartDate).format('YYYY-MM-DD'),
        paymentMethod: paymentMethod || null,
        nonStripePaymentMethod: billingPlan.salesPortalRequiresPaymentMethod
          ? null
          : NonStripePaymentMethod.CASH_OR_CHECK,
        isProrated: form.values.package.allowProration,
        isTapToPay: false,
        successorPackage: null,
        successorBillingPlan: null,
        successorStartDate: null,
        successorBillingStartDate: null,
        successorStatusOnStart: successorPackage?.athleteStatusOnStart || null,
        successorStatusOnEnd: successorPackage?.athleteStatusOnEnd || null,
        successorIsProrated: false,
        discount: appliedDiscount?.discountId ?? null,
        applyToAll: false,
      };

      createMutation.mutate(payload, {
        onError: error => {
          errorNotification('Athlete package creation failed', error.response?.data.message);
        },
        onSuccess: () => {
          trackSalesPortalPurchaseCompleted(
            packageData.gym.gymId,
            packageData.packageId,
            packageData.type,
            billingPlan.billingPlanId,
            billingPlan.numberOfInstallments,
            'athlete_app',
            null,
            'athlete_app',
          );
          setNoPaymentTaken(isFirstInstallmentFree(billingPlan, appliedDiscount));
          setState('Confirmation');
        },
      });
    }
  };

  useEffect(() => {
    if (!!packageData && !!billingPlan) {
      form.setFieldValue('package', packageData);
      form.setFieldValue('autoRenew', packageData.autoRenew);
      form.setFieldValue('billingPlan', billingPlan);
      form.setFieldValue('autoPay', billingPlan.salesPortalRequiresPaymentMethod);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingPlan, packageData]);

  if (createMutation.isLoading) {
    return (
      <Center my='xl'>
        <Loader />
      </Center>
    );
  }

  return (
    <Stack>
      <Stack spacing='sm' pb='lg' px='xl'>
        <PackageSummary pckg={packageData} />

        <Divider size='sm' my='sm' w='100%' />

        <BillingPlanSummary
          gym={packageData.gym}
          billingPlan={billingPlan}
          taxRate={packageData?.taxRate?.rate || 0}
          appliedDiscount={appliedDiscount}
        />

        {successorPackage && successorBillingPlan && (
          <>
            <Divider size='sm' my='sm' w='100%' />
            <PackageSummary pckg={successorPackage} />
            <Divider size='sm' my='sm' w='100%' />
            <BillingPlanSummary
              gym={packageData.gym}
              billingPlan={successorBillingPlan}
              taxRate={successorPackage.taxRate?.rate || 0}
            />
          </>
        )}

        <DiscountCodeInput
          athleteId={athleteId}
          productId={packageData.product}
          value={appliedDiscount}
          onChange={setAppliedDiscount}
          packageTotalCents={firstInstallmentPriceCents}
        />

        <Divider size='sm' my='sm' w='100%' />

        <Group position='apart'>
          <Stack spacing='xs'>
            <Stack spacing={0}>
              <Text size='md' weight='bold' color='dimmed'>
                Package Start Date
              </Text>
              {getPackageStartDate()}
            </Stack>
            <Stack spacing={0}>
              <Text size='md' weight='bold' color='dimmed' mr='xs'>
                Billing Date
              </Text>
              {getPackageStartDate()}
            </Stack>
            <Stack spacing={0}>
              <Text size='md' weight='bold' color='dimmed' mr='xs'>
                Athlete
              </Text>
              <Text size='sm' color='dimmed'>
                {fullName}
              </Text>
            </Stack>
          </Stack>
          <Stack spacing='xs'>
            <Stack spacing={0}>
              <Text size='md' weight='bold' color='dimmed' mr='xs'>
                Auto Renew
              </Text>
              <Text size='sm' color='dimmed'>
                {form.values.autoRenew ? 'On' : 'Off'}
              </Text>
            </Stack>
            <Stack spacing={0}>
              <Text size='md' weight='bold' color='dimmed' mr='xs'>
                Auto Pay
              </Text>
              <Text size='sm' color='dimmed'>
                {form.values.autoPay ? 'On' : 'Off'}
              </Text>
            </Stack>
            <Stack spacing={0}>
              <Text size='md' weight='bold' color='dimmed' mr='xs'>
                Payment Method
              </Text>
              {isError && (
                <Text size='sm' color='red'>
                  Not found
                </Text>
              )}
              {isLoading && <Skeleton h='lg' />}
              {isSuccess && data?.data && (
                <Group noWrap>
                  {data.data.isLocked && <IconLock color='red' size='10px' />}
                  <Text size='sm' color='dimmed'>
                    {getPaymentMethodName(data.data)}
                  </Text>
                </Group>
              )}
              {!paymentMethod && (
                <Text size='sm' color='dimmed'>
                  N/A
                </Text>
              )}
            </Stack>
          </Stack>
        </Group>
      </Stack>
      {form.values.package?.invoiceTrigger === 'immediately' && (
        <Group position='center'>
          <Text color='red' size='sm' fs='italic' data-cy='errorCheckPackageStartDate'>
            The first invoice on this package is due today and will be charged immediately.
          </Text>
        </Group>
      )}
      <Group position='right'>
        <Button variant='subtle' onClick={handleCloseAndReset} disabled={createMutation.isLoading}>
          Cancel
        </Button>
        <Button
          type='submit'
          loading={createMutation.isLoading}
          disabled={isLoading || isError}
          onClick={handleSubmit}
        >
          Submit
        </Button>
      </Group>
    </Stack>
  );
};

export default Review;
